import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Pencil, SlidersHorizontal } from 'lucide-react';
import {
  AggregationMode,
  BALANCED_DEFAULT,
  REALTIME_PRESET,
  QUIET_PRESET,
  deriveAggregationMode,
  type AlertAggregationConfig,
} from '@kirin/api-client/contracts/alert-aggregation';
import { Severity } from '@kirin/api-client/contracts/shared/severity';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { SeverityBadge } from './policylist/components/SeverityBadge';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/hooks/api/errorHelpers';
import { useIsOwnerOrManager } from '@/hooks/useRoleCheck';
import {
  useUserAlertAggregationSettings,
  useUpdateUserAlertAggregationSettings,
  useOrgAlertAggregationSettings,
  useUpdateOrgAlertAggregationSettings,
} from '@/hooks/api/useAlertAggregationSettings';
import { useSettingsUnsavedChanges } from '@/components/settings/SettingsUnsavedChangesContext';
import { useUnsavedChangeActions } from '@/hooks/useScopeChangeHooks';

interface InAppAlertsAggregationControlProps {
  /** Organization ID. If undefined, shows personal (singleton) settings. */
  organizationId?: string;
}

type SeverityKey =
  | 'criticalMinutes'
  | 'highMinutes'
  | 'mediumMinutes'
  | 'lowMinutes';

/** Draft holds the four windows as raw strings so empty/decimal input is detectable. */
interface Draft {
  criticalMinutes: string;
  highMinutes: string;
  mediumMinutes: string;
  lowMinutes: string;
  suppressRepeatedSummaries: boolean;
}

const SEVERITY_FIELDS: ReadonlyArray<{
  key: SeverityKey;
  label: string;
  severity: Severity;
}> = [
  { key: 'criticalMinutes', label: 'Critical', severity: Severity.Critical },
  { key: 'highMinutes', label: 'High', severity: Severity.High },
  { key: 'mediumMinutes', label: 'Medium', severity: Severity.Medium },
  { key: 'lowMinutes', label: 'Low', severity: Severity.Low },
];

const PRESETS: ReadonlyArray<{
  mode: AggregationMode;
  label: string;
  helper: string;
  config: AlertAggregationConfig;
}> = [
  {
    mode: AggregationMode.RealTime,
    label: 'Real-time',
    helper: 'Every security alert shows immediately. No aggregation.',
    config: REALTIME_PRESET,
  },
  {
    mode: AggregationMode.Balanced,
    label: 'Balanced (recommended)',
    helper: 'Recommended defaults. Fast on Critical, calmer on Low.',
    config: BALANCED_DEFAULT,
  },
  {
    mode: AggregationMode.Quiet,
    label: 'Quiet',
    helper: 'Longer windows, fewer interruptions.',
    config: QUIET_PRESET,
  },
];

// Persistent hint on the "Summary Timing by Severity" header (per spec mockup).
const EDITABLE_HINT = 'Editable - change any value to customize';
const INTERVAL_ERROR = 'Enter a whole number from 0 to 120';

function configToDraft(config: AlertAggregationConfig): Draft {
  return {
    criticalMinutes: String(config.criticalMinutes),
    highMinutes: String(config.highMinutes),
    mediumMinutes: String(config.mediumMinutes),
    lowMinutes: String(config.lowMinutes),
    suppressRepeatedSummaries: config.suppressRepeatedSummaries,
  };
}

/** Strip metadata (e.g. updatedAt) down to the bare config fields. */
function pickConfig(config: AlertAggregationConfig): AlertAggregationConfig {
  return {
    criticalMinutes: config.criticalMinutes,
    highMinutes: config.highMinutes,
    mediumMinutes: config.mediumMinutes,
    lowMinutes: config.lowMinutes,
    suppressRepeatedSummaries: config.suppressRepeatedSummaries,
  };
}

/** A value is valid only if it is a non-empty whole number within 0..120. */
function isValidInterval(raw: string): boolean {
  if (!/^\d+$/.test(raw.trim())) return false;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n <= 120;
}

/** Parse a draft into a config, or null when any field is invalid. */
function draftToConfig(d: Draft): AlertAggregationConfig | null {
  const raws = [
    d.criticalMinutes,
    d.highMinutes,
    d.mediumMinutes,
    d.lowMinutes,
  ];
  if (raws.some(r => !isValidInterval(r))) return null;
  const allZero = raws.every(r => Number(r) === 0);
  return {
    criticalMinutes: Number(d.criticalMinutes),
    highMinutes: Number(d.highMinutes),
    mediumMinutes: Number(d.mediumMinutes),
    lowMinutes: Number(d.lowMinutes),
    suppressRepeatedSummaries: allZero ? false : d.suppressRepeatedSummaries,
  };
}

/**
 * Control for configuring in-app alert aggregation windows + suppression.
 *
 * - Personal context (no organizationId): the user controls their own settings.
 * - Organization context: owners/managers edit, members view-only.
 *
 * This is a thin query wrapper: it resolves the persisted config + role, then
 * mounts {@link AggregationForm} with that config as its initial value. Keeping
 * the form behind a "data ready" gate lets it seed local state once at mount,
 * so a background refetch can never clobber the user's in-progress edits.
 */
export function InAppAlertsAggregationControl({
  organizationId,
}: InAppAlertsAggregationControlProps): ReactElement {
  const { isOwnerOrManager, isLoading: isRoleCheckLoading } =
    useIsOwnerOrManager(organizationId);
  const canEdit = organizationId ? isOwnerOrManager : true;

  // Both hook sets are instantiated unconditionally (rules of hooks); the org
  // query is gated by organizationId and the org mutation is never called in
  // personal context.
  const userQuery = useUserAlertAggregationSettings();
  const orgQuery = useOrgAlertAggregationSettings(organizationId ?? '');
  const userMutation = useUpdateUserAlertAggregationSettings();
  const orgMutation = useUpdateOrgAlertAggregationSettings(
    organizationId ?? ''
  );

  const query = organizationId ? orgQuery : userQuery;
  const mutation = organizationId ? orgMutation : userMutation;

  if (query.isLoading || isRoleCheckLoading) {
    return (
      <Card>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            Loading settings...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (query.isError) {
    return (
      <Card>
        <CardContent>
          <div className="rounded-md bg-destructive/15 p-4">
            <p className="text-sm text-destructive font-medium">
              Failed to load alert aggregation settings
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Please try again later or contact support if the problem persists.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const initialConfig: AlertAggregationConfig = query.data
    ? pickConfig(query.data)
    : { ...BALANCED_DEFAULT };

  return (
    // Key by scope so switching org (or org↔personal) remounts the form with a
    // fresh seed from the new scope's config. React Router reuses this component
    // when the :organizationId param changes; without the key the once-at-mount
    // seed would show the previous org's values while the mutation targets the
    // new org (review: GiorgeCh). A background refetch keeps the same key, so
    // the anti-clobber seeding below is preserved.
    <AggregationForm
      key={organizationId ?? 'personal'}
      initialConfig={initialConfig}
      canEdit={!!canEdit}
      organizationId={organizationId}
      mutation={mutation}
    />
  );
}

interface AggregationFormProps {
  initialConfig: AlertAggregationConfig;
  canEdit: boolean;
  organizationId?: string;
  mutation: ReturnType<typeof useUpdateUserAlertAggregationSettings>;
}

function AggregationForm({
  initialConfig,
  canEdit,
  organizationId,
  mutation,
}: AggregationFormProps): ReactElement {
  const { toast } = useToast();

  // Seeded once at mount; never re-derived from props, so background refetches
  // can't overwrite edits. A scope change (org switch) remounts this component
  // via its `key` in the parent, giving a fresh seed for the new scope.
  // `savedConfig` tracks the last persisted value for dirty comparison and is
  // advanced only on a successful save.
  const [draft, setDraft] = useState<Draft>(() => configToDraft(initialConfig));
  const [savedConfig, setSavedConfig] =
    useState<AlertAggregationConfig>(initialConfig);
  // Remembers the user's last Custom configuration so switching to a preset
  // (and back) doesn't lose their typed values — the Custom chip stays
  // available and restores them.
  const [lastCustom, setLastCustom] = useState<AlertAggregationConfig | null>(
    deriveAggregationMode(initialConfig) === AggregationMode.Custom
      ? initialConfig
      : null
  );

  const fieldErrors: Record<SeverityKey, boolean> = {
    criticalMinutes: !isValidInterval(draft.criticalMinutes),
    highMinutes: !isValidInterval(draft.highMinutes),
    mediumMinutes: !isValidInterval(draft.mediumMinutes),
    lowMinutes: !isValidInterval(draft.lowMinutes),
  };
  const hasErrors = Object.values(fieldErrors).some(Boolean);

  const allZero =
    !hasErrors && SEVERITY_FIELDS.every(({ key }) => Number(draft[key]) === 0);

  // Real-time (all-zero) forces suppression off and disabled.
  const effectiveSuppress = allZero ? false : draft.suppressRepeatedSummaries;

  const parsedConfig: AlertAggregationConfig | null = hasErrors
    ? null
    : {
        criticalMinutes: Number(draft.criticalMinutes),
        highMinutes: Number(draft.highMinutes),
        mediumMinutes: Number(draft.mediumMinutes),
        lowMinutes: Number(draft.lowMinutes),
        suppressRepeatedSummaries: effectiveSuppress,
      };

  const currentMode = parsedConfig
    ? deriveAggregationMode(parsedConfig)
    : AggregationMode.Custom;

  // Dirty = the draft differs from the saved config. Computed from the raw
  // field strings so it is independent of validity: a transient typo that
  // doesn't change the underlying values won't be treated as a change, and an
  // invalid field is not auto-flagged as "dirty".
  const isDirty =
    draft.criticalMinutes !== String(savedConfig.criticalMinutes) ||
    draft.highMinutes !== String(savedConfig.highMinutes) ||
    draft.mediumMinutes !== String(savedConfig.mediumMinutes) ||
    draft.lowMinutes !== String(savedConfig.lowMinutes) ||
    effectiveSuppress !== savedConfig.suppressRepeatedSummaries;

  // Warn on hard browser navigation (refresh/close/external) only when there
  // are valid, saveable unsaved changes — never purely because of inline
  // validation errors, since Save is disabled in that state anyway.
  useEffect(() => {
    if (!isDirty || hasErrors) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, hasErrors]);

  // Register the in-app discard guard so switching settings tabs prompts
  // "Discard unsaved changes?" while edits are pending (spec KIR-3253 #14).
  // Gate on valid, saveable changes only — matching the beforeunload guard —
  // so an invalid-but-edited form (Save disabled) doesn't prompt on tab switch.
  const { registerGuard } = useSettingsUnsavedChanges();
  const shouldGuardRef = useRef(isDirty && !hasErrors);
  useEffect(() => {
    shouldGuardRef.current = isDirty && !hasErrors;
  }, [isDirty, hasErrors]);
  useEffect(() => {
    registerGuard(() => shouldGuardRef.current);
    return () => registerGuard(null);
  }, [registerGuard]);

  // Also register with the GLOBAL dirty-state guard so navigating away from the
  // page entirely — clicking a side-menu item, switching org — prompts the same
  // "Discard unsaved changes?" dialog (spec KIR-3253 #14: "navigate away from
  // the page OR away from the tab"). The settings tab guard above only covers
  // intra-Settings tab switches; sidebar / org-switch navigation flows through
  // useGuardedNavigation → ScopeChangeProvider, which blocks on this scope and
  // renders the same UnsavedChangesPopup. Gated on valid, saveable changes to
  // match the tab + beforeunload guards.
  const { markDirty, markClean } = useUnsavedChangeActions();
  const dirtyScopeId = organizationId
    ? `alert-aggregation-${organizationId}`
    : 'alert-aggregation-personal';
  useEffect(() => {
    if (isDirty && !hasErrors) markDirty(dirtyScopeId);
    else markClean(dirtyScopeId);
    return () => markClean(dirtyScopeId);
  }, [isDirty, hasErrors, markDirty, markClean, dirtyScopeId]);

  // If a draft resolves to a Custom configuration, remember it so the user can
  // return to it after clicking a preset.
  const rememberIfCustom = (d: Draft): void => {
    const cfg = draftToConfig(d);
    if (cfg && deriveAggregationMode(cfg) === AggregationMode.Custom) {
      setLastCustom(cfg);
    }
  };

  const setSeverity = (key: SeverityKey, value: string): void => {
    const next = { ...draft, [key]: value };
    setDraft(next);
    rememberIfCustom(next);
  };

  const applyPreset = (config: AlertAggregationConfig): void => {
    setDraft(configToDraft(config));
  };

  const handleSuppressToggle = (nextValue: boolean): void => {
    const next = { ...draft, suppressRepeatedSummaries: nextValue };
    setDraft(next);
    rememberIfCustom(next);
  };

  const handleSave = (): void => {
    if (!canEdit || !parsedConfig || !isDirty) return;
    mutation.mutate(parsedConfig, {
      onSuccess: saved => {
        // Sync the form to the persisted values so it's pristine again
        // (clears "Unsaved changes" without waiting on a refetch).
        const persisted = pickConfig(saved);
        setSavedConfig(persisted);
        setDraft(configToDraft(persisted));
        // Keep the remembered Custom config in sync with what was persisted:
        // if a custom config was saved, the chip should restore that; if a
        // preset was saved, drop the stale pre-save custom.
        setLastCustom(
          deriveAggregationMode(persisted) === AggregationMode.Custom
            ? persisted
            : null
        );
        toast({
          title: 'Settings saved',
          description: 'In-App Alerts Aggregation updated successfully',
        });
      },
      onError: error => {
        toast({
          title: 'Error saving settings',
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      },
    });
  };

  const handleReset = (): void => {
    if (!canEdit) return;
    applyPreset(BALANCED_DEFAULT);
  };

  // Helper under the mode buttons shows only for the three presets; Custom has
  // no preset helper there (its guidance is the persistent editable hint on the
  // Summary Timing header).
  const presetHelper = PRESETS.find(p => p.mode === currentMode)?.helper;

  return (
    <Card data-testid="alert-aggregation-control" data-mode={currentMode}>
      <CardContent className="space-y-6">
        {organizationId && !canEdit && (
          <p
            data-testid="aggregation-readonly-notice"
            className="text-sm text-muted-foreground"
          >
            Only owners and managers can change this setting.
          </p>
        )}

        <p className="text-sm text-muted-foreground">
          Choose how often Kirin combines in-app security alerts into one
          summary based on severity. The first alert in each window is shown
          immediately; based on severity, additional alerts are collected and
          summarized when the window ends. Shorter windows mean more immediate
          alerts; longer windows mean fewer interruptions.
          <br />
          Note: The Alert Notifications setting overrides the aggregation
          configuration. For example, if alerts are set to “Do not notify on
          alerts”, no in-app alerts or summary notifications will be sent.
        </p>

        {/* Aggregation Mode */}
        <div className="space-y-2">
          <Label>Aggregation Mode</Label>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map(preset => (
              <Button
                key={preset.mode}
                data-testid={`aggregation-preset-${preset.mode}`}
                type="button"
                variant={currentMode === preset.mode ? 'default' : 'outline'}
                size="sm"
                disabled={!canEdit}
                onClick={() => applyPreset(preset.config)}
              >
                {preset.label}
              </Button>
            ))}
            {/* Custom appears once the user has a custom configuration and
                stays selectable so a preset click never discards their values:
                clicking it restores the last Custom config. */}
            {(currentMode === AggregationMode.Custom ||
              lastCustom !== null) && (
              <Button
                data-testid="aggregation-preset-custom"
                type="button"
                variant={
                  currentMode === AggregationMode.Custom ? 'default' : 'outline'
                }
                size="sm"
                disabled={!canEdit || lastCustom === null}
                onClick={() => {
                  if (lastCustom) applyPreset(lastCustom);
                }}
                className="gap-1.5"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Custom
              </Button>
            )}
          </div>
          {/* Reserve one line so switching to Custom (no preset helper) does
              not collapse this row and shift the section below. */}
          <p className="min-h-5 text-sm text-muted-foreground">
            {presetHelper}
          </p>
        </div>

        {/* Summary Timing by Severity */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <Label>Summary Timing by Severity</Label>
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Pencil className="h-3.5 w-3.5" />
              {EDITABLE_HINT}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Set how long Kirin aggregates in-app security alerts before sending
            one summary notification based on severity. 0 = real-time. Max 120
            minutes.
          </p>
          <div className="space-y-3">
            {SEVERITY_FIELDS.map(({ key, label, severity }) => (
              <div key={key} className="flex items-center gap-3">
                <div className="flex items-center gap-3 rounded-md border border-border p-2">
                  <SeverityBadge
                    riskLevel={severity}
                    showIcon
                    label={label}
                    className="w-28 justify-center"
                  />
                  <Input
                    id={`agg-${key}`}
                    data-testid={`aggregation-window-${label.toLowerCase()}`}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={120}
                    step={1}
                    aria-label={`${label} aggregation window in minutes`}
                    className={cn(
                      'w-24',
                      fieldErrors[key]
                        ? 'border-destructive'
                        : draft[key] !== String(savedConfig[key]) &&
                            'border-status-warn-emphasis'
                    )}
                    aria-invalid={fieldErrors[key]}
                    value={draft[key]}
                    disabled={!canEdit}
                    onChange={e => setSeverity(key, e.target.value)}
                  />
                  <span className="text-sm text-muted-foreground">minutes</span>
                </div>
                {fieldErrors[key] && (
                  <span
                    data-testid={`aggregation-error-${label.toLowerCase()}`}
                    className="text-sm text-destructive"
                  >
                    {INTERVAL_ERROR}.
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Suppress Repeated Summaries */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Label htmlFor="suppressRepeatedSummaries">
                Suppress Repeated Summaries
              </Label>
              {allZero && (
                <span className="text-sm text-muted-foreground">
                  Not available in Real-time mode because no summaries are
                  generated.
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              After 3 consecutive summaries in a row for the same severity,
              Kirin pauses popup summaries for 60 minutes and shows a status-bar
              badge instead.
            </p>
          </div>
          <Switch
            id="suppressRepeatedSummaries"
            data-testid="aggregation-suppress-toggle"
            checked={effectiveSuppress}
            onCheckedChange={handleSuppressToggle}
            disabled={!canEdit || allZero}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
          <Button
            data-testid="aggregation-save"
            type="button"
            size="sm"
            disabled={!canEdit || hasErrors || !isDirty || mutation.isPending}
            onClick={handleSave}
          >
            Save
          </Button>
          <Button
            data-testid="aggregation-reset"
            type="button"
            variant="outline"
            size="sm"
            disabled={!canEdit || mutation.isPending}
            onClick={handleReset}
          >
            Reset to Defaults
          </Button>
          {hasErrors ? (
            <span
              data-testid="aggregation-save-blocked"
              className="text-sm text-destructive"
            >
              Please fix the highlighted values to save.
            </span>
          ) : isDirty ? (
            <span
              data-testid="aggregation-unsaved-indicator"
              className="flex items-center gap-1.5 text-sm text-status-warn-emphasis"
            >
              <span className="h-2 w-2 rounded-full bg-status-warn-emphasis" />
              Unsaved changes
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
